import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content, images } from '../mockData';

const AssetSection = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const assetContent = content[language].asset;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="asset" 
      ref={sectionRef}
      className="section section-ivory"
    >
      <div className="container">
        {/* Title */}
        <div className={`text-center mb-64 fade-in-up ${isVisible ? 'visible' : ''}`}>
          <h2>{assetContent.title}</h2>
        </div>

        {/* Current State */}
        <div className="grid-2 mb-64">
          <div className={`fade-in-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.2s' }}>
            <h3 className="mb-24" style={{ fontSize: '1.5rem' }}>{assetContent.current.title}</h3>
            <p>{assetContent.current.description}</p>
          </div>
          <div 
            className={`image-container fade-in-up ${isVisible ? 'visible' : ''}`}
            style={{ height: '350px', transitionDelay: '0.3s' }}
          >
            <img src={images.estate.main} alt="Current State" />
          </div>
        </div>

        {/* Physical Components */}
        <div className="mb-64">
          <div className={`text-center mb-48 fade-in-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.4s' }}>
            <h3 style={{ fontSize: '1.5rem' }}>{assetContent.components.title}</h3>
          </div>
          
          {/* Estate Map Image */}
          <div 
            className={`image-container fade-in-up ${isVisible ? 'visible' : ''}`}
            style={{ 
              height: '500px', 
              marginBottom: '48px',
              transitionDelay: '0.5s'
            }}
          >
            <img src="/estate_map_detail.jpeg" alt="Estate Map Detail" style={{ objectFit: 'contain' }} />
          </div>

          <div className="grid-2" style={{ gap: '32px' }}>
            {assetContent.components.items.map((item, index) => (
              <div
                key={item}
                className={`fade-in-up ${isVisible ? 'visible' : ''}`}
                style={{
                  padding: '24px 32px',
                  background: '#ffffff',
                  border: '1px solid rgba(0,0,0,0.1)',
                  transitionDelay: `${0.6 + index * 0.1}s`
                }}
              >
                <p style={{ fontSize: '0.9375rem', margin: 0 }}>{item}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Strategic Location */}
        <div>
          <div className={`fade-in-up ${isVisible ? 'visible' : ''}`} style={{ transitionDelay: '0.8s' }}>
            <h3 className="mb-24" style={{ fontSize: '1.5rem' }}>{assetContent.location.title}</h3>
            <p style={{ marginBottom: '32px' }}>{assetContent.location.description}</p>
            
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {assetContent.location.mapLink && (
                <a 
                  href={assetContent.location.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 28px',
                    background: 'var(--charcoal)',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    border: '1px solid var(--charcoal)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = 'var(--charcoal)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'var(--charcoal)';
                    e.target.style.color = 'white';
                  }}
                >
                  View Route from Airport →
                </a>
              )}
              
              {assetContent.location.airportArticle && (
                <a 
                  href={assetContent.location.airportArticle}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 28px',
                    background: 'var(--olive)',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '0.9375rem',
                    fontWeight: 500,
                    transition: 'all 0.3s ease',
                    border: '1px solid var(--olive)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = 'white';
                    e.target.style.color = 'var(--olive)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'var(--olive)';
                    e.target.style.color = 'white';
                  }}
                >
                  Airport Expansion News →
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AssetSection;
